import { Delete, Edit } from '@mui/icons-material';
import {
  Avatar,
  Button,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemSecondaryAction,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { MDXEditorStyled } from 'components/editableTextField/mdxeditor';
import moment from 'moment';
import React from 'react';
import { ScratchpadMessage } from 'shared';

type MessageWithMetadata = {
  message: ScratchpadMessage;
  currentMessageMoment: moment.Moment;
  showDivider: boolean;
  isPreviousMessageWithin30Minutes: boolean;
};

type MessageListProps = {
  messagesWithMetadata: MessageWithMetadata[];
  editingMessageId: string | null;
  editingContent: string;
  setEditingContent: (content: string) => void;
  startEdit: (message: ScratchpadMessage) => void;
  cancelEdit: () => void;
  saveEdit: () => void;
  sending: boolean;
  onDelete: (message: ScratchpadMessage) => void;
  messageHeaderExtraComponent?: (props: {
    message: ScratchpadMessage;
  }) => React.ReactNode;
};

type MessageItemProps = {
  message: ScratchpadMessage;
  currentMessageMoment: moment.Moment;
  showDivider: boolean;
  isPreviousMessageWithin30Minutes: boolean;
  isEditing: boolean;
  editingContent: string;
  setEditingContent: (content: string) => void;
  startEdit: (message: ScratchpadMessage) => void;
  cancelEdit: () => void;
  saveEdit: () => void;
  sending: boolean;
  onDelete: (message: ScratchpadMessage) => void;
  editingMessageId: string | null;
  MessageHeaderExtraComponent?: (props: {
    message: ScratchpadMessage;
  }) => React.ReactNode;
};

const MessageItem = React.memo<MessageItemProps>(
  ({
    message,
    currentMessageMoment,
    showDivider,
    isPreviousMessageWithin30Minutes,
    isEditing,
    editingContent,
    setEditingContent,
    startEdit,
    cancelEdit,
    saveEdit,
    sending,
    onDelete,
    editingMessageId,
    MessageHeaderExtraComponent,
  }) => {
    return (
      <React.Fragment>
        {showDivider && (
          <Divider sx={{ my: 2 }}>
            {currentMessageMoment.format('MMMM D, YYYY')}
          </Divider>
        )}
        <ListItem
          sx={{
            alignItems: 'flex-start',
          }}
        >
          <ListItemAvatar sx={{ mt: 0.5 }}>
            {isPreviousMessageWithin30Minutes ? (
              <Typography
                variant="overline"
                className="CombinedMessage-time"
                sx={{
                  opacity: 0,
                  lineHeight: 1,
                  display: 'flex',
                }}
              >
                {currentMessageMoment.format('h:mm A')}
              </Typography>
            ) : (
              <Avatar>U</Avatar>
            )}
          </ListItemAvatar>
          <Stack flexGrow={1}>
            {!isPreviousMessageWithin30Minutes && (
              <Stack gap={1} direction="row" alignItems="center">
                <Typography fontWeight="bold">User</Typography>
                <Typography variant="body2" color="text.secondary">
                  &mdash;
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {currentMessageMoment.format('M/D/YYYY h:mm A')}
                </Typography>
                {MessageHeaderExtraComponent && (
                  <MessageHeaderExtraComponent message={message} />
                )}
              </Stack>
            )}
            {isEditing ? (
              <Stack gap={1}>
                <TextField
                  multiline
                  fullWidth
                  value={editingContent}
                  onChange={(e) => setEditingContent(e.target.value)}
                  minRows={3}
                  maxRows={10}
                />
                <Stack direction="row" gap={1} justifyContent="flex-end">
                  <Button size="small" onClick={cancelEdit}>
                    Cancel
                  </Button>
                  <Button size="small" onClick={saveEdit} disabled={sending}>
                    Save
                  </Button>
                </Stack>
              </Stack>
            ) : (
              <MDXEditorStyled
                readOnly
                key={message.content}
                markdown={message.content}
                sx={{
                  width: '100%',
                  '& p': {
                    m: 0,
                  },
                  '._contentEditable_uazmk_379': {
                    p: 0,
                  },
                  '.mdxeditor-toolbar': {
                    p: '0 !important',
                  },
                  h1: {
                    fontSize: '2rem',
                  },
                  h2: {
                    fontSize: '1.7rem',
                  },
                  h3: {
                    fontSize: '1.5rem',
                  },
                  h4: {
                    fontSize: '1.3rem',
                  },
                  h5: {
                    fontSize: '1.2rem',
                  },
                  h6: {
                    fontSize: '1rem',
                  },
                }}
              />
            )}
          </Stack>
          <ListItemSecondaryAction
            sx={{
              opacity: 0,
            }}
          >
            <Stack direction="row" spacing={1}>
              <IconButton
                size="small"
                onClick={() => startEdit(message)}
                disabled={editingMessageId !== null}
              >
                <Edit />
              </IconButton>
              <IconButton color="error" onClick={() => onDelete(message)}>
                <Delete />
              </IconButton>
            </Stack>
          </ListItemSecondaryAction>
        </ListItem>
      </React.Fragment>
    );
  },
  (prevProps, nextProps) => {
    // Only re-render if relevant props actually changed
    return (
      prevProps.message.id === nextProps.message.id &&
      prevProps.message.content === nextProps.message.content &&
      prevProps.currentMessageMoment.valueOf() ===
        nextProps.currentMessageMoment.valueOf() &&
      prevProps.showDivider === nextProps.showDivider &&
      prevProps.isPreviousMessageWithin30Minutes ===
        nextProps.isPreviousMessageWithin30Minutes &&
      prevProps.isEditing === nextProps.isEditing &&
      (prevProps.isEditing
        ? prevProps.editingContent === nextProps.editingContent &&
          prevProps.sending === nextProps.sending
        : true) &&
      prevProps.editingMessageId === nextProps.editingMessageId
    );
  },
);

MessageItem.displayName = 'MessageItem';

export function MessageList(props: MessageListProps) {
  const {
    messagesWithMetadata,
    editingMessageId,
    editingContent,
    setEditingContent,
    startEdit,
    cancelEdit,
    saveEdit,
    sending,
    onDelete,
    messageHeaderExtraComponent: MessageHeaderExtraComponent,
  } = props;

  return (
    <List
      sx={{
        display: 'flex',
        flexDirection: 'column',
        '.MuiListItem-container': {
          '&:hover': {
            bgcolor: 'action.hover',
            '.MuiListItemSecondaryAction-root': {
              opacity: 1,
            },
            '.CombinedMessage-time': {
              opacity: 1,
            },
          },
        },
      }}
    >
      {messagesWithMetadata.map(
        ({
          message,
          currentMessageMoment,
          showDivider,
          isPreviousMessageWithin30Minutes,
        }) => (
          <MessageItem
            key={message.id}
            message={message}
            currentMessageMoment={currentMessageMoment}
            showDivider={showDivider}
            isPreviousMessageWithin30Minutes={isPreviousMessageWithin30Minutes}
            isEditing={editingMessageId === message.id}
            editingContent={editingContent}
            setEditingContent={setEditingContent}
            startEdit={startEdit}
            cancelEdit={cancelEdit}
            saveEdit={saveEdit}
            sending={sending}
            onDelete={onDelete}
            editingMessageId={editingMessageId}
            MessageHeaderExtraComponent={MessageHeaderExtraComponent}
          />
        ),
      )}
    </List>
  );
}
